/** Addressable Matrix Diff projections on the change-detail route. */

import { changeDetailHref, projectHref } from './nav.js';

export const CHANGE_PROJECTION_IDS = [
  'summary',
  'semantic',
  'behavior',
  'graph',
  'evidence',
  'security',
  'performance',
  'proof',
  'context',
  'source',
] as const;

export type ChangeProjectionId = (typeof CHANGE_PROJECTION_IDS)[number];

export const CHANGE_PROJECTION_LABELS: Record<ChangeProjectionId, string> = {
  summary: 'Overview',
  semantic: 'Semantic',
  behavior: 'Behavior',
  graph: 'Dependencies',
  evidence: 'Evidence',
  security: 'Security',
  performance: 'Performance',
  proof: 'Proof',
  context: 'Context',
  source: 'Diff',
};

export type ChangeTab = {
  id: string;
  label: string;
  href: string;
  count?: number;
  projected?: boolean;
};

export function isChangeProjectionId(value: string): value is ChangeProjectionId {
  return (CHANGE_PROJECTION_IDS as readonly string[]).includes(value);
}

export function parseChangeProjectionId(value: string | null | undefined): ChangeProjectionId {
  if (value === undefined || value === null) return 'summary';
  return isChangeProjectionId(value) ? value : 'summary';
}

/** `?view=` from the address bar. Missing or illegal values collapse to Summary. */
export function requestedChangeView(): ChangeProjectionId {
  if (typeof globalThis.location === 'undefined') return 'summary';
  return parseChangeProjectionId(new URLSearchParams(globalThis.location.search ?? '').get('view'));
}

/** Optional graph-node selection on the Graph projection. */
export function requestedProjectionNode(): string | null {
  if (typeof globalThis.location === 'undefined') return null;
  return new URLSearchParams(globalThis.location.search ?? '').get('node');
}

export function changeProjectionHref(
  id: string,
  view: ChangeProjectionId,
  node?: string | null,
): string {
  const href = changeDetailHref(id, view);
  return node ? `${href}&node=${encodeURIComponent(node)}` : href;
}

export function changeProjectionTabs(
  id: string | null,
  counts: Partial<Record<ChangeProjectionId, number>> = {},
): ReadonlyArray<ChangeTab> {
  return CHANGE_PROJECTION_IDS.map((view) => ({
    id: view,
    label: CHANGE_PROJECTION_LABELS[view],
    href: id ? changeProjectionHref(id, view) : projectHref('changes'),
    count: counts[view],
  }));
}

/**
 * The Matrix Diff projections a change actually has.
 *
 * This list used to open with the mockup's order, which interleaved
 * Conversation, Compositions and Checks among the real projections. None of
 * the three had a surface behind it: each rendered as a tab that could not be
 * opened, pointing back at the page it was already on.
 */
export function changeDetailTabs(
  id: string | null,
  counts: Partial<Record<ChangeProjectionId, number>> = {},
): ReadonlyArray<ChangeTab> {
  const matrix = changeProjectionTabs(id, counts);
  const byId = new Map(matrix.map((tab) => [tab.id, tab]));
  return [
    byId.get('summary'),
    byId.get('evidence'),
    byId.get('graph'),
    byId.get('source'),
    byId.get('semantic'),
    byId.get('behavior'),
    byId.get('security'),
    byId.get('performance'),
    byId.get('proof'),
    byId.get('context'),
  ].filter((tab): tab is ChangeTab => tab !== undefined);
}
